import React, { useEffect, useRef, useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import FilterIconFunnel from '../../../../../media/resources/icons/filterIcon.svg'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../hooks/gtmHelpers'
import AddRecipientModal from './AddRecipientModal'
import { useAuth } from '../../../auth'
import { deleteSecurityPermission, getEmailList, getSecurityPermission, getSecurityPermissionExportCsv } from '../../core/_requests'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { NoDataPage } from '../../../resources/components/NoDataPage'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import { DeleteClassificationModal } from '../RecordsClassifications/DeleteClassificationModal'
import { useStakeholders } from '../../../stakeholders/Stakeholders'
import { Filter } from '../../../widgets/components/General/Filter'
import { useParams } from 'react-router-dom'
// import {DeleteClassificationModal} from '../RecordsClassifications/DeleteClassificationModal'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

export const SecurityAndPermissionsDataTable = ({ metaData }: { metaData: any }) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const { companyId, currentUser } = useAuth()
  const { recordId } = useParams()
  const { formatMessage } = useIntl()
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [showKebabMenu, setShowKebabMenu] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [deleteClassificationId, setDeleteClassificationId] = useState<string | null>(null)
  const [isModalActive, setIsModalActive] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState<boolean>(false)
  const [editSecurityData, setEditSecurityData] = useState<any>()
  const [emailList, setEmailList] = useState<any[]>([])
  const [addRecipientButtondisabled, setAddRecipientButtondisabled] = useState<boolean>(false)

  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }

  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    // Check if the search term is in the email or the combined name fields
    const emailMatch = rankItem(row.original.user.email, value)
    const nameMatch = rankItem(`${row.original.user.firstName} ${row.original.user.lastName}`, value)

    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (emailMatch.passed && nameMatch.passed) {
      bestMatch = emailMatch.rank < nameMatch.rank ? emailMatch : nameMatch
    } else if (emailMatch.passed) {
      bestMatch = emailMatch
    } else if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }

  const { setSecurityPermissionFilter, selectedSecurityPermissionTypeFilter } = useStakeholders()

  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const [data, setData] = useState<any[]>([])

  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })

  const getAllSecurityAndPermission = async () => {
    if (!companyId || !recordId) return
    setIsLoading(true)
    try {
      const {
        data: { data, success, errors },
      } = await getSecurityPermission(
        companyId,
        recordId,
        pagination.currentPage,
        pagination.pageSize,
        selectedSecurityPermissionTypeFilter
      )
      if (success) {
        setData(data?.data)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          pageSize: data.pagination.pageSize,
          totalCount: data.pagination.totalRecords,
          nextPage: data.pagination.nextPage,
          prevPage: data.pagination.prevPage,
          totalPages: data.pagination.totalPages,
        }))
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getAllSecurityAndPermission()
  }, [companyId, pagination.currentPage, pagination.pageSize, selectedSecurityPermissionTypeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        cell: (info) => info.row.original.user.firstName + ' ' + info.row.original.user.lastName,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: 'Email',
        cell: (info) => info.row.original.user.email,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'role',
        id: 'role',
        header: 'Role',
        cell: (info) => {
          let roles = info.getValue()

          let bgColor = '#FFC90524' // Default (Viewer - Yellow)
          let textColor = 'text-warning'

          if (roles === 'Editor') {
            bgColor = '#4776E624' // Editor - Blue
            textColor = 'text-primary'
          } else if (roles === 'Admin' || (roles === 'Onwer' && currentUser?.userId !== info.row.original.userId)) {
            roles = "Admin"
            bgColor = '#FF000024'
            textColor = 'text-danger'
          }

          return (
            <div
              className={`text-center px-8 py-2 badge ${textColor}`}
              style={{ background: bgColor }}
            >
              {roles}
            </div>
          )
        },
        filterFn: 'includesString',
      },

      {
        accessorKey: 'createdAt',
        id: 'added_on',
        header: 'Added On',
        cell: (info) =>
          info.getValue() ? format(new Date(info.getValue()), 'MMM dd, yyyy, hh:mm a') : '',
        filterFn: 'includesString',
      },
      {
        accessorKey: 'lastAccessedMinutesAgo',
        id: 'Last Active',
        header: 'Last Active',
        cell: (info) => {
          const minutes = info.getValue()
          if (!minutes) return '-'

          const days = Math.floor(minutes / (60 * 24))
          const hours = Math.floor((minutes % (60 * 24)) / 60)
          const remainingMinutes = minutes % 60

          let formattedTime = ''
          if (days > 0) formattedTime += `${days}d `
          if (hours > 0) formattedTime += `${hours}h `
          if (remainingMinutes > 0 || formattedTime === '') formattedTime += `${remainingMinutes}m`

          return `${formattedTime} ago`
        },
        filterFn: 'includesString',
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  const handleDeleteClassification = async () => {
    if (!companyId) return
    try {
      setDeleteLoading(true)
      const {
        data: { success, errors },
      } = await deleteSecurityPermission(companyId, deleteClassificationId)
      if (success) {
        toast.success(`User removed successfully`)
        getAllSecurityAndPermission()
        getAllEmailList()
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDeleteLoading(false)
      setOpenDeleteConfirmationModal(false)
    }
  }

  const exportToCsv = async () => {
    if (!companyId || !recordId) return
    try {
      const {
        data: { data: values, success },
      } = await getSecurityPermissionExportCsv(companyId, recordId)
      if (success) {
        toast.success(formatMessage({ id: 'Recipients exported successfully' }))
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'recipients.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    } catch (err) {
      console.log(err)
    }
  }

  const getAllEmailList = async () => {
    if (!recordId || !companyId) return
    try {
      const {
        data: { data, success },
      } = await getEmailList(companyId, recordId)
      if (success) {
        if (data?.length === 0) {
          setAddRecipientButtondisabled(true)
        } else {
          setAddRecipientButtondisabled(false)
        }
        const formattedData = data.map((item: any) => ({
          id: item.userId,
          name: item.name,
          value: item.name,
          role: item.role,
        }))

        setEmailList(formattedData)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getAllEmailList()
  }, [recordId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* For Desktop */}
      <div className='d-md-flex justify-content-between mb-4 gap-2 gap-md-0 d-none'>
        <div className='h-100 bg-white d-flex align-items-center rounded height-36px
        '>
          <button
            className='text-clr88 btn btn-sm btn-flex fw-semibold font-size-13 py-4 py-md-3'
            title='Sort and Filter'
            type='button'
            onClick={() => setSecurityPermissionFilter(true)}
          >
            <img src={FilterIconFunnel} alt='' />
            <span className='d-none d-md-block ms-2'>{formatMessage({ id: 'Filter' })}</span>
            {selectedSecurityPermissionTypeFilter ? '*' : ''}
          </button>
        </div>
        <div className='d-flex align-items-center gap-2 w-100 w-md-auto'>
          <form className=' align-items-center w-100 w-md-auto d-flex'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 height-36 rounded-2 font-size-13 font-weight-400'
              placeholder='Search'
            />
          </form>
          {currentUser?.userId === metaData?.createdBy && <button
            className='btn bg-primary text-white custom-button-property height-36 py-0'
            disabled={addRecipientButtondisabled}
            onClick={() => setIsModalActive(true)}
          >
            {formatMessage({ id: 'Add Recipients' })}
          </button>
          }
          <div className='position-relative align-self-top'>
            <div
              className='p-3 cursor-pointer bg-white rounded-2 d-flex justify-content-center align-items-center height-36'
              onClick={() => {
                setShowKebabMenu(!showKebabMenu)
              }}
            >
              <img src={threeDots} alt='' width={16} height={16} />
            </div>
            {showKebabMenu && (
              <div
                className='card shadow rounded top-4 cursor-pointer z-index-1 position-absolute w-150px'
                style={{ top: '40px', right: 0 }}
                ref={dropdownRef}
              >
                <div className='border-bottom border-gray-300 border-bottom-2 p-2 dropdown-items w-100'>
                  <button
                    type='button'
                    className='m-0 border-0 bg-transparent text-dark font-size-13 bg-hover-muted'
                    onClick={() => exportToCsv()}
                  >
                    {formatMessage({ id: 'Export to CSV' })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* For Mobile */}
      <div className='d-flex flex-column gap-2 d-md-none'>
        <div className='d-flex w-100 gap-2'>
          <div className='h-100 bg-white d-flex align-items-center rounded height-36
        '>
            <button
              className='text-clr88 btn btn-sm btn-flex fw-semibold font-size-13 py-4 py-md-3'
              title='Sort and Filter'
              type='button'
              onClick={() => setSecurityPermissionFilter(true)}
            >
              <img src={FilterIconFunnel} alt='' />
              <span className='d-none d-md-block ms-2'>{formatMessage({ id: 'Filter' })}</span>
              {selectedSecurityPermissionTypeFilter ? '*' : ''}
            </button>
          </div>
          <form className=' align-items-center w-100 w-md-auto d-flex w-100'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 height-36 rounded-2 font-size-13 font-weight-400'
              placeholder='Search'
            />
          </form>
        </div>
        <div className='d-flex w-100 gap-2 mb-4'>
          {currentUser?.userId === metaData?.createdBy && <button
            className='btn bg-primary text-white custom-button-property height-36 py-0 w-100'
            disabled={addRecipientButtondisabled}
            onClick={() => setIsModalActive(true)}
          >
            {formatMessage({ id: 'Add Recipients' })}
          </button>
          }
          <div className='position-relative align-self-top'>
            <div
              className='p-3 cursor-pointer bg-white rounded-2 d-flex justify-content-center align-items-center height-36'
              onClick={() => {
                setShowKebabMenu(!showKebabMenu)
              }}
            >
              <img src={threeDots} alt='' width={16} height={16} />
            </div>
            {showKebabMenu && (
              <div
                className='card shadow rounded top-4 cursor-pointer z-index-1 position-absolute w-150px'
                style={{ top: '40px', right: 0 }}
                ref={dropdownRef}
              >
                <div className='border-bottom border-gray-300 border-bottom-2 p-2 dropdown-items w-100'>
                  <button
                    type='button'
                    className='m-0 border-0 bg-transparent text-dark font-size-13 bg-hover-muted'
                    onClick={() => exportToCsv()}
                  >
                    {formatMessage({ id: 'Export to CSV' })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!isLoading && (data?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-13 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className='min-w-120px pb-6 pt-4'>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id} className='border-bottom'>
                    {row.getVisibleCells().map((cell, index) => (
                      <>
                        <td key={cell.id} className='pt-5 border-0'>
                          <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                        </td>
                      </>
                    ))}
                    {row.original?.role !== 'OWNER' && (
                      <td className='pt-5' onClick={(e) => e.stopPropagation()}>
                        <div className='dropdown-container d-flex justify-content-center align-items-center pb-1'>
                          {(currentUser?.userId === metaData?.createdBy ||
                            row.original.userId === currentUser?.userId) &&
                            <Dropdown
                              show={index === selectedIndex}
                              onToggle={(isOpen, event) => {
                                if (event.source === 'click') {
                                  setSelectedIndex(isOpen ? index : undefined)
                                } else {
                                  setSelectedIndex(undefined)
                                }
                              }}
                            >
                              <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                                <img src={threeDots} alt='' className='three-dots-image' />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className='m-2'>
                                <Dropdown.Item
                                  className='text-danger ps-5'
                                  onClick={() => {
                                    setOpenDeleteConfirmationModal(true)
                                    setDeleteClassificationId(row.original?.recordFileUserId)
                                  }}
                                >
                                  <p className='mb-0'>{formatMessage({ id: 'Delete' })}</p>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom'>
        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-md-0 mb-4'>
          <select
            aria-label='Default select example'
            className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
            style={{ outline: 'none' }}
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value)
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0, // Reset to first page when page size changes
              }))
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>

          <span className='font-size-13 text-center text-md-start'>
            {formatMessage({
              id: `Showing ${pagination.currentPage === 1
                ? 1
                : (pagination.currentPage - 1) * pagination.pageSize + 1
                } to ${pagination.nextPage ? data.length : pagination.totalCount} of ${pagination.totalCount
                } records`,
            })}
          </span>
        </div>

        {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First
            onClick={() => handlePageChange(0)}
            disabled={pagination.currentPage === 0}
          >
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.prevPage}
          >
            {'<'}
          </Pagination.Prev>

          {Array.from({ length: pagination.totalPages }, (_, i) => (
            <Pagination.Item
              key={i}
              active={i === pagination.currentPage - 1}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.nextPage}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={!pagination.nextPage}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination> */}
        <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          {/* First Page Button */}
          <Pagination.First
            onClick={() => handlePageChange(0)}
            disabled={pagination.currentPage === 0}
          >
            {'<<'}
          </Pagination.First>

          {/* Previous Page Button */}
          <Pagination.Prev
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {/* Dynamic Page Number Buttons */}
          {Array.from({ length: pagination.totalPages }, (_, i) => (
            <Pagination.Item
              key={i}
              active={i === pagination.currentPage - 1}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1} {/* Display pages as 1-based index */}
            </Pagination.Item>
          ))}

          {/* Next Page Button */}
          <Pagination.Next
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage >= pagination.totalPages - 1}
          >
            {'>'}
          </Pagination.Next>

          {/* Last Page Button */}
          <Pagination.Last
            onClick={() => handlePageChange(pagination.totalPages - 1)}
            disabled={pagination.currentPage >= pagination.totalPages - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination>

      </div>
      <AddRecipientModal
        isActive={isModalActive}
        setIsActive={setIsModalActive}
        getAllSecurityAndPermission={getAllSecurityAndPermission}
        editSecurityData={editSecurityData}
        setEditSecurityData={setEditSecurityData}
        isLoading={isLoading}
        emailList={emailList}
        getAllEmailList={getAllEmailList}
      />
      <DeleteClassificationModal
        setDeleteShowModal={setOpenDeleteConfirmationModal}
        showDeleteModal={openDeleteConfirmationModal}
        handleDelete={handleDeleteClassification}
        deleteLoading={deleteLoading}
        headingMessage={`Delete Security Permission`}
        description={`Are you sure you want to delete this security & permission?`}
      />
      <Filter />
    </>
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
